// eslint-disable-next-line import/no-unresolved
import KeenSlider from 'https://cdn.skypack.dev/pin/keen-slider@v6.6.10-6UolF5LZuIaZoDlY8wmT/mode=imports,min/optimized/keen-slider.js'
const { createElement } = window.BAO.Utils
const useDots = (dots = []) => {
  if (!dots.length) return null

  return carousel => {
    const updateDotStates = () => {
      dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', carousel.track.details.rel === index)
      })
    }

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => carousel.moveToIdx(index))
    })

    carousel.on('created', updateDotStates)
    carousel.on('slideChanged', updateDotStates)
  }
}

class ProductCarousel extends window.BAO.CustomElement() {
  static get styles () {
    return `
      :host {
        display: block;
      }
    `
  }

  static get requiredElements () {
    return ['slides', 'slide']
  }

  connectedCallback () {
    super.connectedCallback()

    this.classUp()

    this.attachClickListeners()

    this.carousel = this.setupCarousel()
  }

  setupCarousel () {
    return new KeenSlider(
      this.els.slides.element,
      {
        selector: this.els.slide.selector,
        slides: { perView: 1 },
        loop: true,
      },
      [useDots(this.els.dot.elements)],
    )
  }

  renderTemplate () {
    const template = createElement('div', {
      class: 'prd-MediaZoom-mobile',
      'data-product-media-external-el': 'wrapper',
    })
    template.innerHTML = `
      ${this.renderImages()}

      <ul class="prd-MediaZoom_Dots">${this.renderDots()}</ul>

      <button type="button" class="prd-MediaZoom_Close">
        <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8.828 3.328 3.171 8.985M8.829 8.985 3.172 3.328" stroke="currentColor" stroke-linecap="round"></path>
        </svg>

        <span class="util-ScreenReaderOnly">Close</span>
      </button>
    `

    this.addTouchListeners(template)
    this.addCloseListener(template)

    return template
  }

  renderDots () {
    return this.els.slide.elements
      .map(() => '<li class="prd-MediaZoom_Dot"></li>')
      .join('')
  }

  renderImages () {
    const slidesCount = this.els.slide.elements.length
    const scroller = createElement('div', {
      class: 'prd-MediaZoom_Slides',
      style: `--SlidesWidth:${slidesCount *
        100}vw; --SlidesCount:${slidesCount}`,
    })

    scroller.innerHTML = this.els.slide.elements
      .map(element => {
        if (element.classList.contains('prd-MediaCarousel_Slide-video')) {
          const video = element.querySelector('[data-type="video"]')
          return video ? video.outerHTML : ''
        }
        return element.firstChild.outerHTML
      })
      .join('')

    return `<div class="prd-MediaZoom_Carousel">${scroller.outerHTML}</div>`
  }

  showLightbox (index) {
    const lightboxNode = this.renderTemplate()
    document.documentElement.appendChild(lightboxNode)

    requestAnimationFrame(() => {
      lightboxNode.scrollIntoView()
    })

    const carousel = lightboxNode.querySelector('.prd-MediaZoom_Carousel')

    if (carousel) {
      this.setupCarouselListeners(carousel, lightboxNode)
      const adjustedIndex = this.getAdjustedIndex(index)
      carousel.scrollLeft = adjustedIndex * window.innerWidth
      carousel.dispatchEvent(new Event('scroll'))
      this.pauseAllVideos(carousel)
      this.playVideoIfPresent(carousel, adjustedIndex)
    }
  }

  setupCarouselListeners (carousel, lightboxNode) {
    carousel.addEventListener(
      'scroll',
      this.handleCarouselScroll.bind(this, lightboxNode),
    )
    this.setupDotListeners(carousel, lightboxNode)
  }

  handleCarouselScroll (lightboxNode, ev) {
    const currentIndex = Math.floor(ev.target.scrollLeft / window.innerWidth)

    lightboxNode.querySelectorAll('.prd-MediaZoom_Dot').forEach((dot, i) => {
      dot.classList.toggle('prd-MediaZoom_Dot-active', i === currentIndex)
    })

    this.pauseAllVideos(ev.target)
    this.playVideoIfPresent(ev.target, currentIndex)
  }

  setupDotListeners (carousel, lightboxNode) {
    lightboxNode
      .querySelectorAll('.prd-MediaZoom_Dots > .prd-MediaZoom_Dot')
      .forEach((dot, index) => {
        dot.addEventListener('click', () => {
          lightboxNode
            .querySelectorAll('.prd-MediaZoom_Dot-active')
            .forEach(activeDot => {
              activeDot.classList.remove('prd-MediaZoom_Dot-active')
            })

          dot.classList.add('prd-MediaZoom_Dot-active')

          carousel.scrollLeft = index * window.innerWidth
        })
      })
  }

  addTouchListeners (template) {
    let initialZoom
    template.addEventListener('touchstart', () => {
      initialZoom = window.innerWidth
    })
    template.addEventListener('touchend', () => {
      const isZoomed = initialZoom !== window.innerWidth
      template.classList.toggle('prd-MediaZoom-locked', isZoomed)
      if (isZoomed) {
        const carousel = template.querySelector('.prd-MediaZoom_Carousel')
        carousel.style.setProperty('--scroll-left', `${carousel.scrollLeft}px`)
      }
    })
  }

  addCloseListener (template) {
    template
      .querySelector('.prd-MediaZoom_Close')
      .addEventListener('click', () => template.remove())
  }

  attachClickListeners () {
    this.els.slide.elements.forEach((el, index) => {
      if (!el.classList.contains('prd-MediaCarousel_Slide-video')) {
        el.addEventListener('click', () => this.showLightbox(index))
      }
    })
  }

  classUp () {
    if (!this.els.slides.element.classList.contains('keen-slider')) {
      this.els.slides.element.classList.add('keen-slider')
    }
  }

  getAdjustedIndex (index) {
    return this.els.slide.elements
      .slice(0, index)
      .filter(el => !el.classList.contains('prd-MediaCarousel_Slide-video'))
      .length
  }

  pauseAllVideos (carousel) {
    carousel.querySelectorAll('video').forEach(video => video.pause())
  }

  playVideoIfPresent (carousel, index) {
    const videoSlide = carousel.querySelectorAll('.prd-MediaZoom_Slides > *')[
      index
    ]
    const video = videoSlide.querySelector('video')
    if (video) {
      this.pauseAllVideos(carousel)
      video.play()
    }
  }
}

if (!customElements.get('product-carousel')) {
  customElements.define('product-carousel', ProductCarousel)
}
